import React from 'react';
import { Button, Skeleton } from 'components';
import {
  Container,
  ErrorWarning,
  TimeWindowFormProps,
} from 'components/TimeWindow';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { CenteredContent, Table } from 'components/Table/TableStyles';
import { LagDateBadge } from 'components/LagDateBadge/LagDateBadge';
import { useDefaultLagDate } from 'hooks/useDefaultLagDate';
import { ReturnComponentSearch } from 'components/Select/ReturnComponentSearch';
import { useFormErrors } from 'hooks';

export const CompositeWeightedTimeWindow: React.FC<TimeWindowFormProps> = ({
  index,
}) => {
  const {
    control,
    formState: { errors },
    setValue,
    getValues,
    trigger,
    watch,
  } = useFormContext();
  const timeWindowsFieldName = `timeWindows[${index}]`;
  const lineItemsFieldName = `${timeWindowsFieldName}.lineItems`;
  const weightingComponentsLoading =
    getValues(timeWindowsFieldName).loadingWeightingComponents;

  const { fields, remove } = useFieldArray({
    control,
    name: lineItemsFieldName,
  });
  const watchFieldArray = watch(lineItemsFieldName);
  const controlledFields = fields.map((field, index) => {
    return {
      ...field,
      ...watchFieldArray[index],
    };
  });

  const { getErrorMessage, hasErrors } = useFormErrors({
    errors,
    fieldName: timeWindowsFieldName,
    index,
  });
  const { defaultLagDate } = useDefaultLagDate();

  return (
    <Container key={lineItemsFieldName}>
      <Table>
        <table style={{ maxWidth: '100%' }} className='timeWindowTable'>
          <tbody>
            <tr>
              <th style={{ width: '35%' }}>Weighting Component</th>
              <th style={{ width: '35%' }}>Return Component</th>
              <th>Lag Date</th>
              <th className='center'>Actions</th>
            </tr>
            {controlledFields.length ? (
              controlledFields.map((field: any, lineIndex: number) => {
                return (
                  <tr key={field.id}>
                    <td key='name'>{field.weightingComponentName}</td>
                    <td key='returnComponentResourceId'>
                      <Controller
                        control={control}
                        name={`${lineItemsFieldName}[${lineIndex}].returnComponentResourceId`}
                        render={({ field: { onChange, value } }) => {
                          return (
                            <ReturnComponentSearch
                              size='sm'
                              isPortalled={true}
                              testid={`${lineItemsFieldName}[${lineIndex}].component-search`}
                              onChange={(val) => {
                                setValue(
                                  `${lineItemsFieldName}[${lineIndex}].isLagged`,
                                  val.isLagged
                                );
                                setValue(
                                  `${lineItemsFieldName}[${lineIndex}].customLagDate`,
                                  val.customLagDate
                                );
                                setValue(
                                  `${lineItemsFieldName}[${lineIndex}].returnComponentName`,
                                  val.label
                                );
                                onChange(val.value);
                                trigger(
                                  `${lineItemsFieldName}[${lineIndex}].returnComponentResourceId`
                                );
                              }}
                              onBlur={() => {
                                trigger(
                                  `${lineItemsFieldName}[${lineIndex}].returnComponentResourceId`
                                );
                              }}
                              value={
                                field.returnComponentResourceId
                                  ? {
                                      ...field,
                                      value: field.returnComponentResourceId,
                                      label: field.returnComponentName,
                                    }
                                  : ''
                              }
                              hasError={getErrorMessage(
                                `${lineItemsFieldName}[${lineIndex}].returnComponentResourceId`
                              )}
                            />
                          );
                        }}
                      />
                    </td>
                    <td className='lagDate'>
                      {LagDateBadge({
                        isLagged: field.isLagged,
                        customLagDate: field.customLagDate,
                        defaultLagDate: defaultLagDate,
                      })}
                    </td>
                    <td key='remove'>
                      <CenteredContent>
                        <Button
                          style={{ color: '#F8333C', fontSize: '12px' }}
                          variant='tertiary'
                          onClick={() => {
                            const lineItems = getValues(lineItemsFieldName);
                            const tagIds = lineItems
                              .filter((v: any, i: number) => i !== lineIndex)
                              ?.map(
                                (component: any) =>
                                  component.weightingComponentTagId
                              );

                            try {
                              setValue(
                                `${timeWindowsFieldName}.tagIds`,
                                tagIds.join(','),
                                { shouldDirty: true }
                              );
                              remove(lineIndex);
                            } catch (err) {}
                          }}
                          className='table'
                        >
                          Remove
                        </Button>
                      </CenteredContent>
                    </td>
                  </tr>
                );
              })
            ) : weightingComponentsLoading ? (
              <Skeleton columns={4} rows={1} />
            ) : null}
            {/* TODO: after refresh should be loading data */}
          </tbody>
        </table>
      </Table>
      <ErrorWarning
        hasErrors={hasErrors && fields.length < 1}
        errorMessage='This field is required'
      />
    </Container>
  );
};
